import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { phoneNumberApiService } from "../services/phoneNumberApiService";
import {
  fromPhoneNumberDtoToPhoneNumber,
  type PhoneNumber,
} from "../types/phoneNumberTypes";

const staleTime = 5 * 60 * 1000; // 5 minutes

// Query keys
export const phoneNumberKeys = {
  all: ["phoneNumber"] as const,
  list: (businessId: string, userId?: string) =>
    [...phoneNumberKeys.all, "list", businessId, { userId }] as const,
};

export const usePhoneNumbers = () => {
  const { businessId } = useCurrentBusiness();
  const { user } = useAuth();

  return useQuery<PhoneNumber[], Error>({
    queryKey: phoneNumberKeys.list(businessId || "", user?.id),
    queryFn: async ({ signal }): Promise<PhoneNumber[]> => {
      const dtos = await phoneNumberApiService.getByBusinessId(businessId || "", signal);
      return (dtos || [])
        .map(fromPhoneNumberDtoToPhoneNumber)
        .filter((phone) => phone.businessId === businessId && !phone.deletedAt);
    },
    enabled: !!user && !!businessId,
    staleTime,
  });
};

export const usePhoneNumber = () => {
  const query = usePhoneNumbers();
  return {
    ...query,
    data: query.data?.find((phone) => phone.status === "active" && phone.number.trim()),
  };
};
